/**
 * tenantMenuInfo.js — 메뉴 이름용 "내 테넌트 요약" (2026-09-24)
 *
 * 서버 GET /api/my/tenant-menu-info → { companyCount, primaryCompanyCode, primaryCompanyName }
 *   companyCount = 일일콜을 켠 회사 수. 사용자 확정 이름 규칙(callMenuNaming.js)이 이 값을 본다.
 *
 * status
 *   'pending' — 아직 모름(첫 화면). 이름을 바꾸지 않는다 → 메뉴는 menuConfig 기본 이름 그대로.
 *   'known'   — 받음.
 *   'unknown' — 실패·빈 값·8초 무응답. 이름을 바꾸지 않는다(오늘과 같은 메뉴). 아무도 메뉴를 잃지 않는다.
 *
 * 깜빡임을 줄이려고 마지막 답을 localStorage 에 둔다(보는 사람 편의용 — 같은 사람일 때만 쓴다).
 *   저장소를 못 쓰면 그냥 'pending' 으로 시작한다.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

const LS_KEY = 'tenantMenuInfo.v1';
const TIMEOUT_MS = 8000;
const PENDING = { status: 'pending', companyCount: null, primaryCode: null, primaryName: null };

function currentToken() {
  try { return sessionStorage.getItem('pg_imp_token') || localStorage.getItem('accessToken') || ''; } catch (_) { return ''; }
}

// 토큰 안의 사용자 표시(비밀 아님 — 토큰 자체가 이미 브라우저 저장소에 있다). 저장값이 같은 사람 것인지 가리는 데만 쓴다.
function viewerKey() {
  try {
    const part = currentToken().split('.')[1];
    if (!part) return '';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return String(json.userId || json.user_id || json.sub || json.id || '');
  } catch (_) {
    return '';
  }
}

function readStored() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    const key = viewerKey();
    if (v && key && v.viewer === key && Number(v.companyCount) > 0) {
      return { status: 'known', companyCount: Number(v.companyCount), primaryCode: v.primaryCode || null, primaryName: v.primaryName || null };
    }
  } catch (_) { /* 저장소 불가 */ }
  return null;
}

function store(info) {
  try {
    const key = viewerKey();
    if (key) localStorage.setItem(LS_KEY, JSON.stringify({ viewer: key, companyCount: info.companyCount, primaryCode: info.primaryCode, primaryName: info.primaryName }));
  } catch (_) { /* 저장소 불가 */ }
}

// 요청은 로그인(토큰)당 한 번 — 사이드바·홈 카드·화면 제목이 같이 쓴다.
let cached = null;
let cachedFor = null;

export function loadTenantMenuInfo() {
  const tk = currentToken();
  if (cached && cachedFor === tk) return cached;
  cachedFor = tk;
  const request = apiClient.get('/api/my/tenant-menu-info')
    .then((r) => {
      const n = Number(r.data?.companyCount);
      if (r.ok && Number.isInteger(n) && n > 0) {
        const info = { status: 'known', companyCount: n, primaryCode: r.data.primaryCompanyCode || null, primaryName: r.data.primaryCompanyName || null };
        store(info);
        return info;
      }
      return { ...PENDING, status: 'unknown' };
    })
    .catch(() => ({ ...PENDING, status: 'unknown' }));
  const timeout = new Promise((resolve) => { setTimeout(() => resolve({ ...PENDING, status: 'unknown' }), TIMEOUT_MS); });
  cached = Promise.race([request, timeout]).then((v) => {
    if (v.status !== 'known') cached = null; // 실패는 기억하지 않는다 — 다음 화면에서 다시 묻는다
    return v;
  });
  return cached;
}

/** 내 테넌트 요약. 첫 값은 저장해 둔 답(같은 사람) 또는 'pending'. */
export function useTenantMenuInfo() {
  const [info, setInfo] = useState(() => readStored() || PENDING);
  useEffect(() => {
    let on = true;
    loadTenantMenuInfo().then((v) => {
      if (!on) return;
      // 새로 못 받았으면 저장해 둔 답을 그대로 쓴다(이미 known 이면 unknown 으로 덮지 않는다)
      setInfo((prev) => (v.status === 'known' || prev.status !== 'known' ? v : prev));
    });
    return () => { on = false; };
  }, []);
  return info;
}
